import * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tabs, TabsContent, TabsList, TabsTrigger, underlineTabTriggerClass } from './tabs'

/*
 * Section navigation (PADROES-UI section 1, "Abas de seção"): the underline
 * tabs of the Feed widget card ("Eventos | Em alta"). Pills are for filters
 * and views only; moving between the sections of an item uses these.
 */

/** The trigger of an underline tab: the Feed widget card's, shared (it lives with the Tabs primitives). */
export { underlineTabTriggerClass }

export interface UnderlineTabItem<V extends string = string> {
  value: V
  label: React.ReactNode
  icon?: LucideIcon
  /** A number after the label; `attention` paints it in the primary color (reviews waiting for me). */
  count?: number | null
  countTone?: 'neutral' | 'attention'
  title?: string
  testId?: string
}

export interface UnderlineTabsProps<V extends string = string> {
  items: UnderlineTabItem<V>[]
  value: V
  onChange: (value: V) => void
  'aria-label': string
  size?: 'md' | 'sm'
  /** The active section's content, wired to its tab (`aria-controls`). */
  children?: React.ReactNode
  className?: string
  listClassName?: string
  'data-testid'?: string
}

/**
 * Underline tabs from a list of items: icon, label and count. The scrolling,
 * the edge shadows and bringing the active tab into view live in `TabsList`.
 */
export function UnderlineTabs<V extends string = string>({ items, value, onChange, size = 'md', children, className, listClassName, ...rest }: UnderlineTabsProps<V>) {
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as V)} className={cn('min-w-0', className)} data-testid={rest['data-testid']}>
      <TabsList size={size} aria-label={rest['aria-label']} className={listClassName}>
        {items.map((item) => (
          <TabsTrigger key={item.value} value={item.value} title={item.title} data-testid={item.testId}>
            {item.icon && <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />}
            {item.label}
            {typeof item.count === 'number' && item.count > 0 && (
              <span className={cn('rounded-full px-1.5 text-xs font-medium tabular-nums',
                item.countTone === 'attention' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600')}>{item.count}</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {children !== undefined && (
        <TabsContent value={value} className="mt-0 focus-visible:ring-offset-0" tabIndex={-1}>{children}</TabsContent>
      )}
    </Tabs>
  )
}
